import { Link } from 'react-router-dom';
import { PLATFORMS } from '@clarity/contract';
import type { AdminConnectionEntry, ConnectionState, RollupState } from '@clarity/contract';
import { useConnections } from '../queries/index.js';
import { useI18n } from '../lib/i18n.js';

/**
 * The shell's compact connection rollup (clarity-platform-abstraction task 5.3;
 * Req 5.3, 5.4), built against the signed-off mock
 * `brand/platform-connections-mock.html` — the topbar `.conn-rollup` pill.
 *
 * It replaces the old single-Talabat `ConnectionStatus` badge with a data-driven
 * summary that NAMES every live platform with a colored status dot and hairline
 * separators — "● Talabat | ● Careem". Every label comes from the data: the
 * platforms are exactly the rows `useConnections()` returns (`GET
 * /admin/connections`, reserved excluded server-side), and each name is read from
 * the descriptor `PLATFORMS[key].name`. No platform name is hardcoded anywhere
 * (Req 5.4).
 *
 * It carries NO session material (Req 5.4) — only the platform name + the
 * three-state classification mapped to a dot hue (`connected → ok`,
 * `expired → warn`, `disconnected → bad`), plus a neutral checking dot while the
 * first probe runs.
 *
 * ── Admin-only (placement) ──
 * `GET /admin/connections` is admin-scoped (a client-role principal gets 403), so
 * the rollup is rendered ONLY for admins — see `Shell.tsx`, which gates it on
 * `isAdmin` the same way the admin "Manage" nav entries gate. This component
 * assumes it is mounted for an admin and links to the admin Connections surface.
 *
 * ── Accessibility (Req 5.6, WCAG AA) ──
 * State is never conveyed by dot colour alone: each segment carries an
 * `aria-label` naming the platform AND its state ("Talabat — Connected"), and the
 * whole control is a labelled link. The dots are `aria-hidden`. The connected-dot
 * pulse is honoured/zeroed by the reduced-motion guard in `app.css`.
 */

/** Map a session connection state to the dot hue class (`ok | warn | bad`). */
const SESSION_DOT: Record<ConnectionState, 'ok' | 'warn' | 'bad'> = {
  connected: 'ok',
  expired: 'warn',
  disconnected: 'bad',
};

/** Map a credential-platform roll-up to the dot hue class. */
const ROLLUP_DOT: Record<RollupState, 'ok' | 'warn' | 'bad' | 'neutral'> = {
  connected: 'ok',
  partial: 'warn',
  'needs-credentials': 'bad',
  checking: 'neutral',
};

/** One dot hue per entry, from its session status or its credential roll-up. */
function entryDot(entry: AdminConnectionEntry): 'ok' | 'warn' | 'bad' | 'neutral' {
  return entry.authModel === 'session' ? SESSION_DOT[entry.status] : ROLLUP_DOT[entry.rollup];
}

/** The state label for an entry (its session state, or its roll-up state). */
function entryStateKey(entry: AdminConnectionEntry): string {
  return entry.authModel === 'session'
    ? `connections.state.${entry.status}`
    : `connections.state.${entry.rollup}`;
}

export function ConnectionRollup() {
  const { t } = useI18n();
  const query = useConnections();

  const rows: AdminConnectionEntry[] =
    query.data?.status === 200 ? query.data.body : [];

  // While the first probe runs, show a neutral "checking" rollup rather than a
  // misleading connected/disconnected state. The query polls, so a re-login is
  // reflected on the next tick without any manual refresh.
  if (query.isPending) {
    return (
      <span className="conn-rollup conn-rollup--checking" role="status">
        <span className="conn-rollup__dot" aria-hidden="true" />
        {t('connections.rollup.checking')}
      </span>
    );
  }

  // The admin read failed with nothing to fall back on (transport, or a 403 that
  // should not happen since the shell only mounts this for admins). Keep it
  // quiet and informative rather than blanking the topbar.
  if (query.isError || query.data?.status !== 200) {
    return (
      <span className="conn-rollup conn-rollup--error" role="status">
        <span className="conn-rollup__dot" aria-hidden="true" />
        {t('connections.rollup.unavailable')}
      </span>
    );
  }

  // The read succeeded but no platform is live yet — nothing to summarise.
  if (rows.length === 0) return null;

  return (
    <Link
      to="/connections"
      className="conn-rollup"
      aria-label={t('connections.rollup.aria')}
      title={t('connections.rollup.title')}
    >
      {rows.map((row, i) => {
        const name = PLATFORMS[row.platform].name;
        const stateLabel = t(entryStateKey(row) as 'connections.state.connected');
        return (
          <span key={row.platform} className="conn-rollup__frag">
            {i > 0 && <span className="conn-rollup__sep" aria-hidden="true" />}
            <span
              className="conn-rollup__platform"
              aria-label={`${name} — ${stateLabel}`}
            >
              <span
                className={`conn-rollup__dot conn-rollup__dot--${entryDot(row)}`}
                aria-hidden="true"
              />
              <span aria-hidden="true">{name}</span>
            </span>
          </span>
        );
      })}
    </Link>
  );
}
